import React, { useMemo } from 'react';
import { useAppContext } from '@/context/AppContext';

interface StatCardProps {
  label: string;
  value: number;
  color?: 'rosa' | 'yellow' | 'red' | 'green' | 'blue';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color = 'rosa' }) => {
  const colorMap = {
    rosa: 'var(--sequel-rosa)',
    yellow: 'var(--sequel-yellow)',
    red: 'var(--sequel-red)',
    green: '#4CAF50',
    blue: '#2196F3'
  };

  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: colorMap[color] }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
};

export const StatisticsDashboard: React.FC = () => {
  const { state } = useAppContext();

  const stats = useMemo(() => {
    if (!state.xmlDoc) {
      return null;
    }

    const groupType = state.ui.selectedGroupType;
    const groupElements = Array.from(state.xmlDoc.querySelectorAll(`${groupType} > Item`));
    
    let totalGroups = 0;
    let totalVehicles = 0;
    let totalDuplicates = 0;
    const categoryCounts: { [key: string]: number } = {};

    groupElements.forEach((element) => {
      totalGroups++;
      const modelElements = element.querySelectorAll('models > Item > Name, models > Item > n');
      const models = Array.from(modelElements).map(el => el.textContent || '').filter(Boolean);
      
      totalVehicles += models.length;
      
      // Count duplicates within this group
      const modelCounts: { [key: string]: number } = {};
      models.forEach(model => {
        modelCounts[model] = (modelCounts[model] || 0) + 1;
        if (modelCounts[model] > 1) {
          totalDuplicates++;
        }
      });

      // Count by category
      models.forEach(model => {
        const vehicle = state.vehicleData.get(model);
        const category = vehicle?.category || 'Unknown';
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      });
    });

    return {
      totalGroups,
      totalVehicles,
      totalDuplicates,
      categoryCounts
    };
  }, [state.xmlDoc, state.ui.selectedGroupType, state.vehicleData]);

  if (!stats) {
    return null;
  }

  // Filter out "Unknown" category
  const filteredCategories = Object.entries(stats.categoryCounts)
    .filter(([category]) => category !== 'Unknown');

  return (
    <div className="statistics-dashboard">
      <h3>Statistics Overview</h3>
      <div className="stats-grid">
        <StatCard label="Total Groups" value={stats.totalGroups} color="rosa" />
        <StatCard label="Total Vehicles" value={stats.totalVehicles} color="yellow" />
        <StatCard label="Duplicates Found" value={stats.totalDuplicates} color="red" />
        <StatCard label="Unique Categories" value={filteredCategories.length} color="blue" />
      </div>
    </div>
  );
};
